import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// CSS Custom Highlight API has been supported on Chrome & Safari for at least 2 years
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { debounce, getScrollParent } from '../../../../Utils';
// now, but its still Nightly-only on Firefox desktop and not supported at all on Firefox Android
export const canUseCSSHiglights = !!('highlights' in CSS);
export const SearchHighlightRenderer = forwardRef(({ shouldHighlightAll, }, ref) => {
    const [editor] = useLexicalComposerContext();
    const rootElement = editor.getRootElement();
    const rootElementRect = useMemo(() => {
        return rootElement === null || rootElement === void 0 ? void 0 : rootElement.getBoundingClientRect();
    }, [rootElement]);
    const [activeHighlightRange, setActiveHighlightRange] = useState();
    const [activeHighlightRect, setActiveHighlightRect] = useState();
    const [rangesToHighlight, setRangesToHighlight] = useState([]);
    const [rangeRects, setRangeRects] = useState([]);
    const isBoundingClientRectVisible = useCallback((rect) => {
        if (!rootElementRect) {
            return false;
        }
        const rangeTop = rect.top;
        const rangeBottom = rect.bottom;
        const isRangeFullyHidden = rangeBottom < rootElementRect.top || rangeTop > rootElementRect.bottom;
        return !isRangeFullyHidden;
    }, [rootElementRect]);
    const getBoundingClientRectForRangeIfVisible = useCallback((range) => {
        const rect = range.getBoundingClientRect();
        if (isBoundingClientRectVisible(rect)) {
            return rect;
        }
        return undefined;
    }, [isBoundingClientRectVisible]);
    const getVisibleRectsFromRanges = useCallback((ranges) => {
        const rects = [];
        if (!rootElementRect) {
            return rects;
        }
        for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];
            if (!range) {
                continue;
            }
            const rangeBoundingRect = range.getBoundingClientRect();
            if (!isBoundingClientRectVisible(rangeBoundingRect)) {
                continue;
            }
            rects.push(rangeBoundingRect);
        }
        return rects;
    }, [isBoundingClientRectVisible, rootElementRect]);
    useImperativeHandle(ref, () => {
        return {
            setActiveHighlight: (range) => {
                if (canUseCSSHiglights) {
                    CSS.highlights.set('active-search-result', new Highlight(range));
                    return;
                }
                setActiveHighlightRange(range);
                setActiveHighlightRect(getBoundingClientRectForRangeIfVisible(range));
            },
            highlightMultipleRanges: (ranges) => {
                if (canUseCSSHiglights) {
                    const searchResultsHighlight = new Highlight();
                    for (let i = 0; i < ranges.length; i++) {
                        const range = ranges[i];
                        if (!range) {
                            continue;
                        }
                        searchResultsHighlight.add(range);
                    }
                    CSS.highlights.set('search-results', searchResultsHighlight);
                    return;
                }
                setRangesToHighlight(ranges);
            },
            clearHighlights: () => {
                if (canUseCSSHiglights) {
                    CSS.highlights.clear();
                    return;
                }
                setRangesToHighlight([]);
                setRangeRects([]);
                setActiveHighlightRange(undefined);
                setActiveHighlightRect(undefined);
            },
        };
    }, [getBoundingClientRectForRangeIfVisible]);
    useEffect(() => {
        if (shouldHighlightAll && !canUseCSSHiglights) {
            setRangeRects(getVisibleRectsFromRanges(rangesToHighlight));
        }
        else {
            setRangeRects([]);
        }
    }, [getVisibleRectsFromRanges, rangesToHighlight, shouldHighlightAll]);
    useEffect(() => {
        if (canUseCSSHiglights) {
            return;
        }
        const rootElementScrollParent = getScrollParent(editor.getRootElement());
        if (!rootElementScrollParent) {
            return;
        }
        const scrollListener = debounce(() => {
            if (activeHighlightRange) {
                setActiveHighlightRect(getBoundingClientRectForRangeIfVisible(activeHighlightRange));
            }
            if (shouldHighlightAll) {
                setRangeRects(getVisibleRectsFromRanges(rangesToHighlight));
            }
        }, 16);
        rootElementScrollParent.addEventListener('scroll', scrollListener);
        return () => {
            rootElementScrollParent.removeEventListener('scroll', scrollListener);
        };
    }, [
        activeHighlightRange,
        editor,
        getBoundingClientRectForRangeIfVisible,
        getVisibleRectsFromRanges,
        rangesToHighlight,
        shouldHighlightAll,
    ]);
    if (canUseCSSHiglights || !rootElementRect) {
        return null;
    }
    return (_jsxs("div", { className: "pointer-events-none absolute left-0 top-0 h-full w-full", children: [activeHighlightRect && (_jsx("div", { className: "active-search-highlight fixed z-[1000]", style: {
                    transform: `translate(${activeHighlightRect.left - rootElementRect.left}px, ${activeHighlightRect.top - rootElementRect.top}px)`,
                    width: `${activeHighlightRect.width}px`,
                    height: `${activeHighlightRect.height}px`,
                } })), rangeRects.map((rect, index) => (_jsx("div", { className: "search-highlight fixed z-[1000]", style: {
                    transform: `translate(${rect.left - rootElementRect.left}px, ${rect.top - rootElementRect.top}px)`,
                    width: `${rect.width}px`,
                    height: `${rect.height}px`,
                } }, index)))] }));
});
